import InfoIcon from '@mui/icons-material/Info';
import { Fade, Theme, Tooltip } from '@mui/material';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import makeStyles from '@mui/styles/makeStyles';
import { Zone } from 'luxon';
import React, { useMemo, useState } from 'react';
import { useRecoilValueLoadable } from 'recoil';
import {
  endpointEnum,
  makeAPIRequest,
} from '../../../services/rest/makeAPIRequest';
import {
  CallInfo,
  ICallTranscriptionItem,
} from '../CallTranscriptions/CallInfo';
import { CallTranscriptionSummary } from '../CallTranscriptions/CallTranscriptionSummary';
import FullCallTranscription from '../CallTranscriptions/FullCallTranscription';
import InfoPanel from '../InfoPanel/InfoPanel';
import { ModalWithStyles } from './ModalWithStyles';

const useStyles = makeStyles((theme: Theme) => ({
  svgIcon: {
    color:
      theme.palette.mode === 'light'
        ? 'rgba(0, 0, 0, 0.54)'
        : 'rgba(255, 255, 255, 0.64)',
  },
}));

export const CallTranscriptionInfoTooltip = (props) => {
  const classes = useStyles();
  return (
    <Tooltip
      style={{ cursor: 'pointer' }}
      TransitionComponent={Fade}
      TransitionProps={{ timeout: 600 }}
      placement="right"
      {...props}
    >
      <InfoIcon className={classes.svgIcon} fontSize="inherit" />
    </Tooltip>
  );
};

const CallTranscriptionModal = (props: {
  handleClose: () => void;
  show: boolean;
  callXd: string;
  businessId: number;
  timezone: string | Zone | undefined;
  callDate: string | undefined;
  callType: string;
  recordingUrl: string;
}) => {
  if (!props?.businessId || !props?.callXd) {
    return null;
  }

  const [tabValue, setTabValue] = useState<number>(0);

  React.useEffect(() => {
    tabValue !== 0 && setTabValue(0);
    return () => setTabValue(0);
  }, []);

  const url = endpointEnum.GET_CALL_TRANSCRIPTION.replace(
    '{businessId}',
    props.businessId.toString()
  ).replace('{callXd}', props.callXd);

  const callSummaryLoadable = useRecoilValueLoadable(
    makeAPIRequest({
      endpoint: url,
      stage: 'itn',
      method: 'GET',
      domain: process.env.VITE_APP_CDK_DOMAIN,
    })
  );

  const initialValues = useMemo(
    () =>
      callSummaryLoadable.state === 'hasValue'
        ? callSummaryLoadable.contents
        : {},
    [callSummaryLoadable.contents]
  );

  return (
    <ModalWithStyles
      show={props.show}
      aria-labelledby="example-modal-sizes-title-sm"
      title={
        tabValue === 1 ? 'Conversation Transcription' : 'Transcription Summary'
      }
      onHide={() => {
        setTabValue(0);
        props.handleClose();
      }}
      isLoading={callSummaryLoadable?.state !== 'hasValue'}
      size="md"
      hideFooter={true}
    >
      <>
        <InfoPanel>
          {tabValue === 1 ? (
            <div>
              View in-depth transcription information about a call here. See the
              entire conversation broken down by the speaker and sentiment. Use
              playback controls to review call details, as well as download the
              call to listen offline. Switch to Transcription Summary for
              at-a-glance information about this call.
            </div>
          ) : (
            <div>
              View at-a-glance conversation intelligence information about a
              call here. See details like duration, agent use of
              keywords/phrases, and the overall strength of the call. Switch to
              Full Transcription for full conversation details and playback
              controls for this call.
            </div>
          )}
        </InfoPanel>
        <div
          style={{
            marginBottom: '10px',
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
          }}
        >
          <CallInfo
            item={initialValues?.response?.data as ICallTranscriptionItem}
            zone={props.timezone}
            loading={callSummaryLoadable.state !== 'hasValue'}
            callDate={props?.callDate}
          />
          <div>
            <Tabs
              value={tabValue}
              onChange={(_event, newValue) => {
                setTabValue(newValue);
              }}
              indicatorColor="primary"
              textColor="primary"
            >
              <Tab label="Call Summary" />
              <Tab label="Full Transcription" />
            </Tabs>
          </div>
        </div>
        {tabValue === 0 ? (
          <CallTranscriptionSummary
            loading={callSummaryLoadable.state !== 'hasValue'}
            initialValues={initialValues}
          />
        ) : (
          <FullCallTranscription
            loading={callSummaryLoadable.state !== 'hasValue'}
            item={initialValues?.response?.data as ICallTranscriptionItem}
            callDate={props?.callDate}
            callType={props?.callType}
            recordingUrl={props?.recordingUrl}
          />
        )}
      </>
    </ModalWithStyles>
  );
};

export default CallTranscriptionModal;
